import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Barcode,
  KeyRound,
  Pencil,
  Plus,
  Save,
  ShieldCheck,
  ShieldOff,
  Trash2,
  X,
  Zap,
  ZapOff,
} from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { useCapabilitiesStore } from '../../stores/capabilitiesStore';
import { Permission } from '../../auth/permissions';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, checkboxClass } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { useConfirm, type ConfirmOptions } from '../../components/ui/ConfirmDialog';
import { CodeMapSheet } from '../../components/suppliers/CodeMapSheet';

/** GET /admin/suppliers row — DB config + the runtime state of its adapter. */
interface Supplier {
  id: string;
  code: string;
  name: string;
  legal_name: string | null;
  siret: string | null;
  active: boolean;
  api_enabled: boolean;
  api_type: string | null;
  api_base_url: string | null;
  auth_type: string | null;
  timeout_ms: number | null;
  priority: number;
  cutoff_time: string | null;
  /** The hour the parts are on our counter on the promised day (2.1); null = day only. */
  delivery_before_time: string | null;
  delivery_lead_days: number;
  /** The weekly order-day table (migration 166, 2026-10-29); null = the single rule above. */
  delivery_rules: DeliveryRules | null;
  return_delay_days: number | null;
  min_order_value: string | null;
  free_shipping_threshold: string | null;
  reliability_score: string;
  contact: unknown;
  updated_at: string;
  has_credentials: boolean;
  po_90d: number;
  po_90d_received: number;
  mapped_articles: number;
  adapterRegistered: boolean;
  circuitState: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

type HealthStatus = 'OK' | 'MANUAL' | 'NOT_REGISTERED' | 'NO_PROBE_DATA' | 'TIMEOUT' | 'API_ERROR';

interface Health {
  supplierId: string;
  code: string;
  status: HealthStatus;
  latencyMs: number | null;
  message: string;
  circuitState: Supplier['circuitState'];
  offersReturned: number | null;
  checkedAt: string;
}

const HEALTH_TONE: Record<HealthStatus, 'success' | 'warning' | 'destructive' | 'muted'> = {
  OK: 'success',
  MANUAL: 'muted',
  NOT_REGISTERED: 'warning',
  NO_PROBE_DATA: 'warning',
  TIMEOUT: 'destructive',
  API_ERROR: 'destructive',
};

const API_TYPES = ['REST', 'SOAP', 'EDI', 'CUSTOM'] as const;
const AUTH_TYPES = [
  'NONE',
  'BASIC',
  'API_KEY',
  'BEARER',
  'OAUTH2_CLIENT_CREDENTIALS',
  'BODY_CREDENTIALS',
] as const;

/**
 * Credential shape per auth scheme — what the adapters actually read out of the blob.
 *
 * These key names are a CONTRACT, not labels: the sheet posts exactly these strings and the
 * adapter decrypts and reads exactly these strings. A typo here stores a blob the adapter cannot
 * use, and the symptom is a supplier that looks configured ("Credentials" badge, green) but never
 * binds an adapter. Cross-check against the adapter's config interface before editing.
 *
 * BODY_CREDENTIALS is AutoPartner's scheme (CustomerAPI v2.13): no token exchange at all, the
 * three secrets ride in the body of every call. `baseUrl` lives in the blob rather than in the
 * supplier's api_base_url column because AutoPartner's TEST and PRODUCTION environments differ in
 * BOTH address and credentials — see autoPartnerCredentials.ts.
 */
const CREDENTIAL_KEYS: Record<string, string[]> = {
  NONE: [],
  BASIC: ['username', 'password'],
  API_KEY: ['apiKey'],
  BEARER: ['token'],
  OAUTH2_CLIENT_CREDENTIALS: ['clientId', 'clientSecret', 'tokenUrl', 'scope'],
  BODY_CREDENTIALS: ['clientCode', 'wsPassword', 'clientPassword', 'baseUrl', 'currencyCode'],
};

const cid = (): string => crypto.randomUUID();
const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

/**
 * The owner's supplier delivery rules (2026-10-29): one row per ORDER day Monday to Sunday — the
 * cut-off (Paris), the lead when ordered before it, the lead when ordered after it, counted in
 * the SUPPLIER's delivery days — plus the weekdays it delivers on and its non-delivery dates.
 * Any change the client sends is a setting here, never a deploy. A row with no cut-off means the
 * whole day counts as "before" (how "Sunday counts as Monday before cut-off" is written: the
 * Sunday row carries the lead counted from Sunday).
 */
interface DeliveryRules {
  days: Array<{ cutoff: string | null; leadBefore: number; leadAfter: number }>;
  deliveryDays: number[];
  closedDates: string[];
}
interface RulesForm {
  days: Array<{ cutoff: string; leadBefore: string; leadAfter: string }>;
  deliveryDays: number[];
  /** One date per line, "YYYY-MM-DD". */
  closedDates: string;
}
const WEEKDAYS_ISO = [1, 2, 3, 4, 5, 6, 7] as const;
const blankRules = (): RulesForm => ({
  days: WEEKDAYS_ISO.map((d) => ({
    cutoff: d === 7 ? '' : '17:00',
    leadBefore: '1',
    leadAfter: d === 7 ? '1' : '2',
  })),
  deliveryDays: [1, 2, 3, 4, 5],
  closedDates: '',
});
const rulesFormOf = (r: DeliveryRules | null): RulesForm | null =>
  r
    ? {
        days: r.days.map((d) => ({
          cutoff: d.cutoff ?? '',
          leadBefore: String(d.leadBefore),
          leadAfter: String(d.leadAfter),
        })),
        deliveryDays: [...r.deliveryDays],
        closedDates: r.closedDates.join('\n'),
      }
    : null;
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
/** The form → the payload, or an error key when a cell cannot be read. */
const rulesPayload = (
  f: RulesForm
): { ok: true; value: DeliveryRules } | { ok: false; error: string } => {
  const days: DeliveryRules['days'] = [];
  for (const d of f.days) {
    const cutoff = d.cutoff.trim();
    if (cutoff && !HHMM_RE.test(cutoff)) return { ok: false, error: 'rulesBadCutoff' };
    const lb = Number(d.leadBefore);
    const la = Number(d.leadAfter);
    if (!Number.isInteger(lb) || !Number.isInteger(la) || lb < 1 || la < 1 || lb > 30 || la > 30) {
      return { ok: false, error: 'rulesBadLead' };
    }
    days.push({ cutoff: cutoff || null, leadBefore: lb, leadAfter: la });
  }
  if (f.deliveryDays.length === 0) return { ok: false, error: 'rulesNoDeliveryDay' };
  const closedDates = f.closedDates
    .split(/[\n,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (closedDates.some((x) => !YMD_RE.test(x))) return { ok: false, error: 'rulesBadDate' };
  return {
    ok: true,
    value: {
      days,
      deliveryDays: [...f.deliveryDays].sort((a, b) => a - b),
      closedDates: [...new Set(closedDates)].sort(),
    },
  };
};

/** Form state mirrors the wire (camelCase); '' means "empty → null". */
interface FormState {
  code: string;
  name: string;
  legalName: string;
  siret: string;
  active: boolean;
  apiEnabled: boolean;
  apiType: string;
  apiBaseUrl: string;
  authType: string;
  timeoutMs: string;
  priority: string;
  cutoffTime: string;
  deliveryBeforeTime: string;
  deliveryLeadDays: string;
  /** Null = no weekly table: the single cut-off / lead above applies. */
  deliveryRules: RulesForm | null;
  returnDelayDays: string;
  minOrderValue: string;
  freeShippingThreshold: string;
}

const blankForm = (): FormState => ({
  code: '',
  name: '',
  legalName: '',
  siret: '',
  active: true,
  apiEnabled: false,
  apiType: '',
  apiBaseUrl: '',
  authType: '',
  timeoutMs: '',
  priority: '3',
  cutoffTime: '17:00',
  deliveryBeforeTime: '',
  deliveryLeadDays: '1',
  deliveryRules: null,
  returnDelayDays: '30',
  minOrderValue: '',
  freeShippingThreshold: '',
});

const formOf = (s: Supplier): FormState => ({
  code: s.code,
  name: s.name,
  legalName: s.legal_name ?? '',
  siret: s.siret ?? '',
  active: s.active,
  apiEnabled: s.api_enabled,
  apiType: s.api_type ?? '',
  apiBaseUrl: s.api_base_url ?? '',
  authType: s.auth_type ?? '',
  timeoutMs: s.timeout_ms == null ? '' : String(s.timeout_ms),
  priority: String(s.priority),
  cutoffTime: s.cutoff_time ?? '',
  deliveryBeforeTime: s.delivery_before_time ?? '',
  deliveryLeadDays: String(s.delivery_lead_days),
  deliveryRules: rulesFormOf(s.delivery_rules ?? null),
  returnDelayDays: s.return_delay_days == null ? '' : String(s.return_delay_days),
  minOrderValue: s.min_order_value ?? '',
  freeShippingThreshold: s.free_shipping_threshold ?? '',
});

/**
 * Screen 43 — Supplier API configuration (/admin/suppliers).
 *
 * One row per supplier the comparison engine fans out to: how it is reached (API type, base
 * URL, auth scheme, timeout), where it sits in the ranking (priority), its logistics (cut-off,
 * lead days, thresholds) and how it has actually behaved (reliability score, circuit state).
 *
 * Two things are deliberately not editable here:
 *   • the reliability score — SupplierScoreJob computes it weekly from received-vs-total POs,
 *     and the 90-day counts behind it are shown so the number is explainable, not typed over;
 *   • the code — it is the key the adapter registry binds to, so renaming it would unbind the
 *     adapter. Create a new supplier instead.
 *
 * Credentials are write-only: they are posted once, encrypted AES-256-GCM at rest, and the API
 * only ever answers whether something is stored.
 */
export default function Suppliers() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();

  const remove = async (s: Supplier): Promise<void> => {
    const ok = await confirm({
      title: t('adminScreens.suppliers.deleteConfirm.title', { name: s.name, code: s.code }),
      description: t('adminScreens.suppliers.deleteConfirm.description'),
      confirmLabel: t('adminScreens.suppliers.actions.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/admin/suppliers/${s.id}`, { headers: { 'X-Command-Id': cid() } });
      push({
        tone: 'success',
        message: t('adminScreens.suppliers.toast.deleted', { name: s.name }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };
  const canWrite = useCapabilitiesStore((s) => s.permissionSet).has(Permission.SYSTEM_SETTINGS_RW);

  const [rows, setRows] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<Record<string, Health | 'checking'>>({});
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [credentialsFor, setCredentialsFor] = useState<Supplier | null>(null);
  const [codesFor, setCodesFor] = useState<Supplier | null>(null);
  const [apiBusy, setApiBusy] = useState<Record<string, boolean>>({});

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ data: Supplier[] }>('/admin/suppliers');
      setRows(data.data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Row switch for the supplier's API.
   *
   * This is the "turn the key on/off" control: it flips `api_enabled`, which is what the
   * comparison engine actually gates on (api_enabled=false → the supplier is skipped and its
   * lines fall to PURCHASER manual entry). The stored credential is deliberately NOT touched —
   * switching a supplier off and back on must never mean re-typing a secret. Deleting the key is
   * a separate, explicit action behind the Credentials sheet.
   *
   * Optimistic: the row flips immediately and is reverted if the PATCH is refused. The common
   * refusal is a 422 from the server's coherence check — an API-enabled supplier needs both an
   * apiType and an apiBaseUrl — which is exactly the case where the old value was the correct one.
   */
  const toggleApi = async (s: Supplier): Promise<void> => {
    const next = !s.api_enabled;
    setApiBusy((b) => ({ ...b, [s.id]: true }));
    setRows((rs) => rs.map((r) => (r.id === s.id ? { ...r, api_enabled: next } : r)));
    try {
      const { data } = await apiClient.patch<{ data: Supplier }>(
        `/admin/suppliers/${s.id}`,
        { apiEnabled: next },
        { headers: { 'X-Command-Id': cid() } }
      );
      // Take the server's row wholesale: the PATCH response carries the runtime fields
      // (adapterRegistered, circuitState) that the optimistic edit above could not know.
      setRows((rs) => rs.map((r) => (r.id === s.id ? { ...r, ...data.data } : r)));
      push({
        tone: 'success',
        message: next
          ? s.adapterRegistered
            ? t('adminScreens.suppliers.toast.apiEnabled', { name: s.name })
            : t('adminScreens.suppliers.toast.apiEnabledRestart', { name: s.name })
          : t('adminScreens.suppliers.toast.apiDisabled', { name: s.name }),
      });
    } catch (err) {
      setRows((rs) => rs.map((r) => (r.id === s.id ? { ...r, api_enabled: s.api_enabled } : r)));
      showApiError(err);
    } finally {
      setApiBusy((b) => {
        const n = { ...b };
        delete n[s.id];
        return n;
      });
    }
  };

  const runHealthCheck = async (s: Supplier): Promise<void> => {
    setHealth((h) => ({ ...h, [s.id]: 'checking' }));
    try {
      const { data } = await apiClient.post<{ data: Health }>(
        `/admin/suppliers/${s.id}/health-check`,
        {},
        { headers: { 'X-Command-Id': cid() } }
      );
      setHealth((h) => ({ ...h, [s.id]: data.data }));
      // The probe also reports the live breaker state — fold it back into the row so the
      // "Circuit" badge doesn't keep showing what was true at page load.
      setRows((rs) =>
        rs.map((r) => (r.id === s.id ? { ...r, circuitState: data.data.circuitState } : r))
      );
    } catch (err) {
      setHealth((h) => {
        const next = { ...h };
        delete next[s.id];
        return next;
      });
      showApiError(err);
    }
  };

  const columns = useMemo<Column<Supplier>[]>(() => {
    const base: Column<Supplier>[] = [
      {
        key: 'code',
        header: t('adminScreens.suppliers.columns.supplier'),
        sortable: true,
        render: (s) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{s.name}</span>
              {!s.active && <Badge variant="muted">{t('adminScreens.suppliers.inactive')}</Badge>}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">{s.code}</div>
          </div>
        ),
      },
      {
        key: 'api_enabled',
        header: t('adminScreens.suppliers.columns.api'),
        render: (s) => (
          <div className="flex min-w-0 items-start gap-2.5">
            <label
              className={`mt-0.5 flex shrink-0 items-center ${
                canWrite && !apiBusy[s.id] ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
              title={
                canWrite
                  ? s.api_enabled
                    ? t('adminScreens.suppliers.toggle.onTitle')
                    : t('adminScreens.suppliers.toggle.offTitle')
                  : t('adminScreens.suppliers.toggle.permissionRequired')
              }
            >
              <input
                type="checkbox"
                className={checkboxClass}
                checked={s.api_enabled}
                disabled={!canWrite || !!apiBusy[s.id]}
                onChange={() => void toggleApi(s)}
                aria-label={
                  s.api_enabled
                    ? t('adminScreens.suppliers.toggle.disableAria', { name: s.name })
                    : t('adminScreens.suppliers.toggle.enableAria', { name: s.name })
                }
              />
            </label>
            {s.api_enabled ? (
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Badge variant="default">{s.api_type ?? '—'}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {s.auth_type ?? t('adminScreens.suppliers.noAuth')}
                  </span>
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {s.api_base_url ?? '—'}
                </div>
              </div>
            ) : (
              <Badge variant="muted">{t('adminScreens.suppliers.manualEntry')}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'adapterRegistered',
        header: t('adminScreens.suppliers.columns.wiring'),
        render: (s) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant={s.has_credentials ? 'success' : 'muted'}
              title={
                s.has_credentials
                  ? t('adminScreens.suppliers.wiring.credentialsStoredTitle')
                  : t('adminScreens.suppliers.wiring.noCredentialsTitle')
              }
            >
              {s.has_credentials ? (
                <ShieldCheck className="mr-1 h-3 w-3" />
              ) : (
                <ShieldOff className="mr-1 h-3 w-3" />
              )}
              {s.has_credentials
                ? t('adminScreens.suppliers.wiring.credentials')
                : t('adminScreens.suppliers.wiring.noCredentials')}
            </Badge>
            {s.api_enabled && (
              <Badge
                variant={s.adapterRegistered ? 'success' : 'warning'}
                title={
                  s.adapterRegistered
                    ? t('adminScreens.suppliers.wiring.adapterBoundTitle')
                    : t('adminScreens.suppliers.wiring.noAdapterTitle')
                }
              >
                {s.adapterRegistered ? (
                  <Zap className="mr-1 h-3 w-3" />
                ) : (
                  <ZapOff className="mr-1 h-3 w-3" />
                )}
                {s.adapterRegistered
                  ? t('adminScreens.suppliers.wiring.adapterLive')
                  : t('adminScreens.suppliers.wiring.noAdapter')}
              </Badge>
            )}
            {s.api_enabled && s.circuitState !== 'CLOSED' && (
              <Badge
                variant={s.circuitState === 'OPEN' ? 'destructive' : 'warning'}
                title={t('adminScreens.suppliers.wiring.circuitTitle')}
              >
                {t('adminScreens.suppliers.wiring.circuit', { state: s.circuitState })}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'priority',
        header: t('adminScreens.suppliers.columns.rank'),
        sortable: true,
        render: (s) => (
          <div className="text-xs">
            <div>
              {t('adminScreens.suppliers.priority')} <b>{s.priority}</b>
            </div>
            <div className="text-muted-foreground">
              {s.cutoff_time ?? '—'} ·{' '}
              {t('adminScreens.suppliers.leadDays', { days: s.delivery_lead_days })}
              {s.delivery_before_time &&
                ` · ${t('delivery.before', { time: s.delivery_before_time })}`}
            </div>
          </div>
        ),
      },
      {
        key: 'reliability_score',
        header: t('adminScreens.suppliers.columns.reliability'),
        sortable: true,
        render: (s) => {
          const pct = Math.round(Number(s.reliability_score) * 100);
          const tone = pct >= 90 ? 'bg-success' : pct >= 70 ? 'bg-warning' : 'bg-destructive';
          return (
            <div className="min-w-24" title={t('adminScreens.suppliers.reliabilityTitle')}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-medium">{pct}%</span>
                <span className="text-muted-foreground">
                  {t('adminScreens.suppliers.poCount', {
                    received: s.po_90d_received,
                    total: s.po_90d,
                  })}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        },
      },
      {
        key: 'health',
        header: t('adminScreens.suppliers.columns.lastCheck'),
        noTruncate: true,
        render: (s) => {
          const h = health[s.id];
          if (!h) return <span className="text-xs text-muted-foreground">—</span>;
          if (h === 'checking')
            return (
              <span className="text-xs text-muted-foreground">
                {t('adminScreens.suppliers.probing')}
              </span>
            );
          return (
            <div className="min-w-0 max-w-64">
              <Badge variant={HEALTH_TONE[h.status]}>
                {t(`adminScreens.suppliers.health.${h.status}`, { defaultValue: h.status })}
                {h.latencyMs != null && ` · ${h.latencyMs} ms`}
              </Badge>
              <p className="mt-1 text-xs text-muted-foreground">{h.message}</p>
            </div>
          );
        },
      },
    ];
    if (!canWrite) return base;
    base.push({
      key: 'actions',
      header: '',
      noTruncate: true,
      render: (s) => (
        <div className="flex justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            title={t('adminScreens.suppliers.actions.checkTitle')}
            disabled={health[s.id] === 'checking'}
            onClick={() => void runHealthCheck(s)}
          >
            <Activity className="h-4 w-4" />
            {t('adminScreens.suppliers.actions.check')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCredentialsFor(s)}>
            <KeyRound className="h-4 w-4" />
            {t('adminScreens.suppliers.actions.credentials')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={t('adminScreens.suppliers.actions.codesTitle')}
            onClick={() => setCodesFor(s)}
          >
            <Barcode className="h-4 w-4" />
            {t('adminScreens.suppliers.actions.codes')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(s)}>
            <Pencil className="h-4 w-4" />
            {t('adminScreens.suppliers.actions.edit')}
          </Button>
          {/* 2026-10-30 — a supplier nothing refers to (a test row) can go; one with history is
              refused by the server (409 SUPPLIER_IN_USE) and stays deactivatable. */}
          <Button
            size="sm"
            variant="ghost"
            title={t('adminScreens.suppliers.actions.deleteTitle')}
            onClick={() => void remove(s)}
          >
            <Trash2 className="h-4 w-4" />
            {t('adminScreens.suppliers.actions.delete')}
          </Button>
        </div>
      ),
    });
    return base;
    // apiBusy: the row switch disables itself while its PATCH is in flight, so the cell has to
    // re-render when that map changes.
  }, [canWrite, health, apiBusy, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canWrite) {
    return (
      <div className="p-6 text-muted-foreground">{t('adminScreens.suppliers.noPermission')}</div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('adminScreens.suppliers.title')}
        description={t('adminScreens.suppliers.description')}
        actions={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" />
            {t('adminScreens.suppliers.newSupplier')}
          </Button>
        }
      />

      <DataGrid
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        loading={loading}
        empty={t('adminScreens.suppliers.empty')}
      />

      {editing && (
        <SupplierSheet
          supplier={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {credentialsFor && (
        <CredentialsSheet
          supplier={credentialsFor}
          confirm={confirm}
          onClose={() => setCredentialsFor(null)}
          onSaved={async () => {
            setCredentialsFor(null);
            await load();
          }}
        />
      )}
      {codesFor && <CodeMapSheet supplier={codesFor} onClose={() => setCodesFor(null)} />}
      {confirmDialog}
    </div>
  );
}

// ===================== create / edit =====================
function SupplierSheet({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [f, setF] = useState<FormState>(() => (supplier ? formOf(supplier) : blankForm()));
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]): void =>
    setF((cur) => ({ ...cur, [k]: v }));

  const save = async (): Promise<void> => {
    if (!f.name.trim()) {
      push({ tone: 'warning', message: t('adminScreens.suppliers.form.nameRequired') });
      return;
    }
    if (!supplier && !/^[A-Za-z0-9_]{2,32}$/.test(f.code.trim())) {
      push({ tone: 'warning', message: t('adminScreens.suppliers.form.codeInvalid') });
      return;
    }
    // Mirrors the server's coherence check so the failure is instant rather than a round-trip.
    if (f.apiEnabled && (!f.apiType || !f.apiBaseUrl.trim())) {
      push({ tone: 'warning', message: t('adminScreens.suppliers.form.apiNeedsTypeAndUrl') });
      return;
    }
    let deliveryRules: DeliveryRules | null = null;
    if (f.deliveryRules) {
      const parsed = rulesPayload(f.deliveryRules);
      if (!parsed.ok) {
        push({ tone: 'warning', message: t(`adminScreens.suppliers.form.${parsed.error}`) });
        return;
      }
      deliveryRules = parsed.value;
    }
    const body = {
      name: f.name.trim(),
      legalName: f.legalName.trim() || null,
      siret: f.siret.trim() || null,
      active: f.active,
      apiEnabled: f.apiEnabled,
      apiType: f.apiType || null,
      apiBaseUrl: f.apiBaseUrl.trim() || null,
      authType: f.authType || null,
      timeoutMs: num(f.timeoutMs),
      priority: Number(f.priority),
      cutoffTime: f.cutoffTime.trim() || null,
      deliveryBeforeTime: f.deliveryBeforeTime.trim() || null,
      deliveryLeadDays: Number(f.deliveryLeadDays),
      deliveryRules,
      returnDelayDays: num(f.returnDelayDays),
      minOrderValue: num(f.minOrderValue),
      freeShippingThreshold: num(f.freeShippingThreshold),
    };
    setBusy(true);
    try {
      if (supplier) {
        await apiClient.patch(`/admin/suppliers/${supplier.id}`, body, {
          headers: { 'X-Command-Id': cid() },
        });
        push({
          tone: 'success',
          message: t('adminScreens.suppliers.form.updated', { name: f.name }),
        });
      } else {
        await apiClient.post(
          '/admin/suppliers',
          { ...body, code: f.code.trim().toUpperCase() },
          { headers: { 'X-Command-Id': cid() } }
        );
        push({
          tone: 'success',
          message: t('adminScreens.suppliers.form.created', { name: f.name }),
        });
      }
      await onSaved();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[86vh] w-full max-w-3xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {supplier
                ? t('adminScreens.suppliers.form.editTitle', { name: supplier.name })
                : t('adminScreens.suppliers.newSupplier')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {supplier
                ? t('adminScreens.suppliers.form.codeLocked')
                : t('adminScreens.suppliers.form.codePermanent')}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('adminScreens.suppliers.form.identity')}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t('adminScreens.suppliers.form.code')}>
                <Input
                  value={f.code}
                  disabled={!!supplier}
                  placeholder="INTERCARS"
                  onChange={(e) => set('code', e.target.value.toUpperCase())}
                />
              </Field>
              <Field label={t('adminScreens.suppliers.form.name')}>
                <Input value={f.name} onChange={(e) => set('name', e.target.value)} />
              </Field>
              <Field label={t('adminScreens.suppliers.form.legalName')}>
                <Input value={f.legalName} onChange={(e) => set('legalName', e.target.value)} />
              </Field>
              <Field label="SIRET">
                <Input value={f.siret} onChange={(e) => set('siret', e.target.value)} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                className={checkboxClass}
                type="checkbox"
                checked={f.active}
                onChange={(e) => set('active', e.target.checked)}
              />
              {t('adminScreens.suppliers.form.activeLabel')}
            </label>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('adminScreens.suppliers.form.apiWiring')}</h3>
            <label className="flex items-center gap-2 text-sm">
              <input
                className={checkboxClass}
                type="checkbox"
                checked={f.apiEnabled}
                onChange={(e) => set('apiEnabled', e.target.checked)}
              />
              {t('adminScreens.suppliers.form.apiEnabledLabel')}
            </label>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t('adminScreens.suppliers.form.apiType')}>
                <Select
                  value={f.apiType}
                  disabled={!f.apiEnabled}
                  onChange={(e) => set('apiType', e.target.value)}
                >
                  <option value="">—</option>
                  {API_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('adminScreens.suppliers.form.authScheme')}>
                <Select
                  value={f.authType}
                  disabled={!f.apiEnabled}
                  onChange={(e) => set('authType', e.target.value)}
                >
                  <option value="">—</option>
                  {AUTH_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('adminScreens.suppliers.form.timeout')}
                hint={t('adminScreens.suppliers.form.timeoutHint')}
              >
                <Input
                  inputMode="numeric"
                  value={f.timeoutMs}
                  disabled={!f.apiEnabled}
                  placeholder="3000"
                  onChange={(e) => set('timeoutMs', e.target.value)}
                />
              </Field>
              <Field
                label={t('adminScreens.suppliers.form.priority')}
                hint={t('adminScreens.suppliers.form.priorityHint')}
              >
                <Select value={f.priority} onChange={(e) => set('priority', e.target.value)}>
                  {[1, 2, 3, 4, 5].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label={t('adminScreens.suppliers.form.baseUrl')}>
              <Input
                value={f.apiBaseUrl}
                disabled={!f.apiEnabled}
                placeholder="https://api.supplier.example/v1"
                onChange={(e) => set('apiBaseUrl', e.target.value)}
              />
            </Field>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('adminScreens.suppliers.form.logistics')}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field
                label={t('adminScreens.suppliers.form.cutoff')}
                hint={t('adminScreens.suppliers.form.cutoffHint')}
              >
                <Input
                  value={f.cutoffTime}
                  placeholder="17:00"
                  onChange={(e) => set('cutoffTime', e.target.value)}
                />
              </Field>
              <Field label={t('adminScreens.suppliers.form.leadTime')}>
                <Input
                  inputMode="numeric"
                  value={f.deliveryLeadDays}
                  onChange={(e) => set('deliveryLeadDays', e.target.value)}
                />
              </Field>
              <Field
                label={t('adminScreens.suppliers.form.beforeTime')}
                hint={t('adminScreens.suppliers.form.beforeTimeHint')}
              >
                <Input
                  value={f.deliveryBeforeTime}
                  placeholder="12:00"
                  onChange={(e) => set('deliveryBeforeTime', e.target.value)}
                />
              </Field>
              <Field label={t('adminScreens.suppliers.form.returnWindow')}>
                <Input
                  inputMode="numeric"
                  value={f.returnDelayDays}
                  onChange={(e) => set('returnDelayDays', e.target.value)}
                />
              </Field>
              <Field label={t('adminScreens.suppliers.form.minOrder')}>
                <Input
                  inputMode="decimal"
                  value={f.minOrderValue}
                  onChange={(e) => set('minOrderValue', e.target.value)}
                />
              </Field>
              <Field label={t('adminScreens.suppliers.form.freeShipping')}>
                <Input
                  inputMode="decimal"
                  value={f.freeShippingThreshold}
                  onChange={(e) => set('freeShippingThreshold', e.target.value)}
                />
              </Field>
            </div>
          </section>

          {/* 2026-10-29 — the owner's weekly delivery rules. With the table on, the single
              cut-off / lead above are only the fallback the server keeps for older cached offers. */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">{t('adminScreens.suppliers.form.rules')}</h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={f.deliveryRules != null}
                  onChange={(e) => set('deliveryRules', e.target.checked ? blankRules() : null)}
                />
                {t('adminScreens.suppliers.form.rulesEnabled')}
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('adminScreens.suppliers.form.rulesHint')}
            </p>
            {f.deliveryRules && (
              <div className="space-y-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-2 font-medium">
                        {t('adminScreens.suppliers.form.rulesOrderDay')}
                      </th>
                      <th className="py-1 pr-2 font-medium">
                        {t('adminScreens.suppliers.form.rulesCutoff')}
                      </th>
                      <th className="py-1 pr-2 font-medium">
                        {t('adminScreens.suppliers.form.rulesLeadBefore')}
                      </th>
                      <th className="py-1 pr-2 font-medium">
                        {t('adminScreens.suppliers.form.rulesLeadAfter')}
                      </th>
                      <th className="py-1 font-medium">
                        {t('adminScreens.suppliers.form.rulesDelivers')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKDAYS_ISO.map((iso, i) => {
                      const rules = f.deliveryRules!;
                      const row = rules.days[i];
                      const setRow = (patch: Partial<RulesForm['days'][number]>) =>
                        set('deliveryRules', {
                          ...rules,
                          days: rules.days.map((d, j) => (j === i ? { ...d, ...patch } : d)),
                        });
                      return (
                        <tr key={iso} className="border-t">
                          <td className="py-1 pr-2 font-medium">
                            {(t('delivery.weekdays', { returnObjects: true }) as string[])[iso % 7]}
                          </td>
                          <td className="py-1 pr-2">
                            <Input
                              value={row.cutoff}
                              placeholder="—"
                              className="w-24"
                              onChange={(e) => setRow({ cutoff: e.target.value })}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <Input
                              inputMode="numeric"
                              value={row.leadBefore}
                              className="w-16"
                              onChange={(e) => setRow({ leadBefore: e.target.value })}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <Input
                              inputMode="numeric"
                              value={row.leadAfter}
                              className="w-16"
                              onChange={(e) => setRow({ leadAfter: e.target.value })}
                            />
                          </td>
                          <td className="py-1">
                            <input
                              type="checkbox"
                              className={checkboxClass}
                              checked={rules.deliveryDays.includes(iso)}
                              aria-label={t('adminScreens.suppliers.form.rulesDelivers')}
                              onChange={(e) =>
                                set('deliveryRules', {
                                  ...rules,
                                  deliveryDays: e.target.checked
                                    ? [...rules.deliveryDays, iso]
                                    : rules.deliveryDays.filter((d) => d !== iso),
                                })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Field
                  label={t('adminScreens.suppliers.form.rulesClosedDates')}
                  hint={t('adminScreens.suppliers.form.rulesClosedDatesHint')}
                >
                  <textarea
                    className="w-full rounded border bg-background px-2 py-1.5 text-sm"
                    rows={3}
                    value={f.deliveryRules.closedDates}
                    placeholder="2026-12-25"
                    onChange={(e) =>
                      set('deliveryRules', { ...f.deliveryRules!, closedDates: e.target.value })
                    }
                  />
                </Field>
              </div>
            )}
          </section>

          {supplier && (
            <p className="text-xs text-muted-foreground">
              {t('adminScreens.suppliers.form.reliabilityNote', {
                pct: Math.round(Number(supplier.reliability_score) * 100),
                received: supplier.po_90d_received,
                total: supplier.po_90d,
              })}{' '}
              {t('adminScreens.suppliers.form.mappedArticles', { count: supplier.mapped_articles })}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            <X className="h-4 w-4" />
            {t('adminScreens.suppliers.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={busy}>
            <Save className="h-4 w-4" />
            {busy
              ? t('adminScreens.suppliers.saving')
              : supplier
                ? t('adminScreens.suppliers.form.saveChanges')
                : t('adminScreens.suppliers.form.createSupplier')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===================== credentials (write-only) =====================
function CredentialsSheet({
  supplier,
  confirm,
  onClose,
  onSaved,
}: {
  supplier: Supplier;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const keys = CREDENTIAL_KEYS[supplier.auth_type ?? ''] ?? ['clientId', 'clientSecret'];
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''));
    if (Object.keys(filled).length === 0) {
      push({ tone: 'warning', message: t('adminScreens.suppliers.credentials.fillOne') });
      return;
    }
    setBusy(true);
    try {
      await apiClient.put(
        `/admin/suppliers/${supplier.id}/credentials`,
        { credentials: filled },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({
        tone: 'success',
        title: t('adminScreens.suppliers.credentials.storedTitle'),
        message: t('adminScreens.suppliers.credentials.storedMessage'),
      });
      await onSaved();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const clear = async (): Promise<void> => {
    const ok = await confirm({
      title: t('adminScreens.suppliers.credentials.removeTitle', { name: supplier.name }),
      description: t('adminScreens.suppliers.credentials.removeDescription'),
      confirmLabel: t('adminScreens.suppliers.credentials.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.delete(`/admin/suppliers/${supplier.id}/credentials`, {
        headers: { 'X-Command-Id': cid() },
      });
      push({ tone: 'success', message: t('adminScreens.suppliers.credentials.removed') });
      await onSaved();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {t('adminScreens.suppliers.credentials.title', { name: supplier.name })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {supplier.auth_type ?? t('adminScreens.suppliers.credentials.noAuthScheme')} ·{' '}
              {supplier.has_credentials
                ? t('adminScreens.suppliers.credentials.secretStored')
                : t('adminScreens.suppliers.credentials.nothingStored')}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
            {t('adminScreens.suppliers.credentials.secretsNote')}
          </p>
          {keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('adminScreens.suppliers.credentials.noCredentialsScheme')}
            </p>
          ) : (
            keys.map((k) => (
              <Field key={k} label={k}>
                <Input
                  type={/secret|password|key|token/i.test(k) ? 'password' : 'text'}
                  autoComplete="off"
                  value={values[k] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                />
              </Field>
            ))
          )}
        </div>

        <div className="flex justify-between border-t px-5 py-3">
          <Button
            variant="destructive"
            size="sm"
            disabled={busy || !supplier.has_credentials}
            onClick={() => void clear()}
          >
            <Trash2 className="h-4 w-4" />
            {t('adminScreens.suppliers.credentials.removeSecret')}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              <X className="h-4 w-4" />
              {t('adminScreens.suppliers.cancel')}
            </Button>
            <Button onClick={() => void save()} disabled={busy || keys.length === 0}>
              <KeyRound className="h-4 w-4" />
              {busy
                ? t('adminScreens.suppliers.saving')
                : t('adminScreens.suppliers.credentials.store')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
